import React, { useCallback, useState } from 'react'
import Image from 'next/image'
import cx from 'classnames'
import PropTypes from 'prop-types'
import { Icon } from '@theneatcompany/nui'
import { Text } from '@paper-monster/web/src/components/common'

import styles from './Testimonials.module.scss'

const starIndexes = [1, 2, 3, 4, 5]

const initials = (name) => name.split(' ').map((part) => part[0]).join('')

export default function Testimonials({ t }) {
  const [startIndex, setStartIndex] = useState(0)
  const total = t.items.length
  const visibleCount = 3

  const canPrev = startIndex > 0
  const canNext = startIndex + visibleCount < total
  // Arrows that can never move would be dead controls.
  const hasMore = total > visibleCount

  const goPrev = useCallback(() => {
    if (canPrev) {
      setStartIndex((i) => i - 1)
    }
  }, [canPrev])

  const goNext = useCallback(() => {
    if (canNext) {
      setStartIndex((i) => i + 1)
    }
  }, [canNext])

  const visibleItems = t.items

  return (
    <section className={styles.section}>
      <div className={styles.inner}>
        <Text.H2 className={styles.title}>{t.title}</Text.H2>
        <Text.P className={styles.subtitle}>{t.subtitle}</Text.P>

        <div className={styles.carouselWrap}>
          {hasMore && (
            <button
              aria-label="Previous"
              className={cx(styles.arrowBtn, styles.arrowLeft, { [styles.arrowDisabled]: !canPrev })}
              onClick={goPrev}
            >
              <Icon fill="white" name="chevron-left" />
            </button>
          )}

          <div className={styles.carouselViewport}>
            <div className={styles.cards} style={{ '--slide-index': startIndex }}>
              {visibleItems.map((item) => (
                <div key={item.name} className={styles.card}>
                  <div className={styles.cardTop}>
                    <div className={styles.logoAvatar}>
                      {item.logo
                        ? <Image alt={item.name} height={40} src={item.logo} width={40} />
                        : <Text bold>{initials(item.name)}</Text>}
                    </div>
                    <div className={styles.authorInfo}>
                      <Text className={styles.authorName}>{item.name}</Text>
                      <Text className={styles.authorRole}>{item.role}</Text>
                    </div>
                  </div>
                  <div className={styles.starsRow}>
                    {starIndexes.map((star) => (
                      <Image
                        key={`testimonial-star-${item.name}-${star}`}
                        alt="star"
                        height={13}
                        src="/star-icon.svg"
                        width={14}
                      />
                    ))}
                  </div>
                  <div className={styles.quoteBody}>
                    <Image
                      alt=""
                      className={styles.quoteOpen}
                      height={15}
                      src="/quote-icon.svg"
                      width={15}
                    />
                    <Text.P className={styles.quoteText}>{item.quote}</Text.P>
                    <Image
                      alt=""
                      className={styles.quoteClose}
                      height={15}
                      src="/quote-icon.svg"
                      width={15}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {hasMore && (
            <button
              aria-label="Next"
              className={cx(styles.arrowBtn, styles.arrowRight, { [styles.arrowDisabled]: !canNext })}
              onClick={goNext}
            >
              <Icon fill="white" name="chevron-right" />
            </button>
          )}
        </div>
      </div>
    </section>
  )
}

Testimonials.propTypes = {
  t: PropTypes.shape({
    title: PropTypes.string.isRequired,
    subtitle: PropTypes.string.isRequired,
    items: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string.isRequired,
        role: PropTypes.string.isRequired,
        quote: PropTypes.string.isRequired,
        logo: PropTypes.string,
      }),
    ).isRequired,
  }).isRequired,
}
